import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, currency, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const emptyVehicle = {
  vin: "",
  make: "",
  model: "",
  year: new Date().getFullYear(),
  title: "",
  mileage: 0,
  color: "",
  purchase_price: 0,
  sale_price: "",
  sold_date: "",
  list_month: "",
  status: "in_inventory",
  notes: "",
};

const STATUS_OPTIONS = [
  { value: "in_inventory", label: "In Inventory" },
  { value: "pending", label: "Pending" },
  { value: "maintenance", label: "Maintenance" },
  { value: "sold", label: "Sold" },
];

const statusMeta = {
  in_inventory: { label: "In stock", className: "bg-blue-50 text-blue-700 border-blue-200" },
  pending: { label: "Pending", className: "bg-amber-50 text-amber-700 border-amber-200" },
  maintenance: { label: "Maintenance", className: "bg-violet-50 text-violet-700 border-violet-200" },
  sold: { label: "Sold", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

// Display order: In stock first, then Pending, then Maintenance, then Sold at the bottom.
const STATUS_RANK = { in_inventory: 0, pending: 1, maintenance: 2, sold: 3 };

const SORT_OPTIONS = [
  { value: "status", label: "Status (default)" },
  { value: "newest", label: "Newest added" },
  { value: "oldest", label: "Oldest added" },
  { value: "year_desc", label: "Year (newest → oldest)" },
  { value: "year_asc", label: "Year (oldest → newest)" },
  { value: "profit_desc", label: "Profit (high → low)" },
  { value: "sold_desc", label: "Sold date (newest)" },
  { value: "title_asc", label: "Title (A → Z)" },
  { value: "list_month_asc", label: "List month (earliest)" },
  { value: "list_month_desc", label: "List month (latest)" },
];

function vehicleTitle(v) {
  return (v && v.title && v.title.trim()) || `${v.year} ${v.make} ${v.model}`;
}

// "2026-03" -> "Mar 2026"
function formatListMonth(s) {
  if (!s || typeof s !== "string") return "—";
  const m = /^(\d{4})-(\d{2})$/.exec(s);
  if (!m) return s;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[Number(m[2]) - 1]} ${m[1]}`;
}

// Normalize a vehicle's list_month to canonical "YYYY-MM".
// Handles legacy free-text values like "June", "Jun", "june 2026" by picking a sensible year
// from the vehicle itself (created_at year, else current year).
const MONTH_LOOKUP = {
  jan: "01", january: "01",
  feb: "02", february: "02",
  mar: "03", march: "03",
  apr: "04", april: "04",
  may: "05",
  jun: "06", june: "06",
  jul: "07", july: "07",
  aug: "08", august: "08",
  sep: "09", sept: "09", september: "09",
  oct: "10", october: "10",
  nov: "11", november: "11",
  dec: "12", december: "12",
};

function normalizeListMonth(vehicle) {
  const raw = vehicle && vehicle.list_month;
  if (!raw || typeof raw !== "string") return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  // Already YYYY-MM
  if (/^\d{4}-\d{2}$/.test(trimmed)) return trimmed;
  // "Month YYYY" or "Month, YYYY"
  const withYear = trimmed.match(/^([A-Za-z.]+)[\s,]+(\d{4})$/);
  if (withYear) {
    const mm = MONTH_LOOKUP[withYear[1].toLowerCase().replace(/\./g, "")];
    if (mm) return `${withYear[2]}-${mm}`;
  }
  // Bare month name — use vehicle's own year as fallback (created_at, else current)
  const mm = MONTH_LOOKUP[trimmed.toLowerCase().replace(/\./g, "")];
  if (mm) {
    let year = new Date().getFullYear();
    if (vehicle.created_at) {
      const parsed = new Date(vehicle.created_at);
      if (!Number.isNaN(parsed.getFullYear())) year = parsed.getFullYear();
    }
    return `${year}-${mm}`;
  }
  return trimmed; // unknown format — pass through
}

function compareVehicles(a, b, sortBy) {
  switch (sortBy) {
    case "newest":
      return (b.created_at || "").localeCompare(a.created_at || "");
    case "oldest":
      return (a.created_at || "").localeCompare(b.created_at || "");
    case "year_desc":
      return (b.year || 0) - (a.year || 0);
    case "year_asc":
      return (a.year || 0) - (b.year || 0);
    case "profit_desc":
      return (b.profit || 0) - (a.profit || 0);
    case "sold_desc": {
      const ad = a.sold_date || "";
      const bd = b.sold_date || "";
      if (!ad && !bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
      return bd.localeCompare(ad);
    }
    case "title_asc":
      return vehicleTitle(a).localeCompare(vehicleTitle(b));
    case "list_month_asc": {
      const am = normalizeListMonth(a);
      const bm = normalizeListMonth(b);
      if (!am && !bm) return 0;
      if (!am) return 1;
      if (!bm) return -1;
      return am.localeCompare(bm);
    }
    case "list_month_desc": {
      const am = normalizeListMonth(a);
      const bm = normalizeListMonth(b);
      if (!am && !bm) return 0;
      if (!am) return 1;
      if (!bm) return -1;
      return bm.localeCompare(am);
    }
    case "status":
    default: {
      const ra = STATUS_RANK[a.status] ?? 99;
      const rb = STATUS_RANK[b.status] ?? 99;
      if (ra !== rb) return ra - rb;
      return 0;
    }
  }
}

export default function Vehicles() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyVehicle);
  const [saving, setSaving] = useState(false);
  const [sortBy, setSortBy] = useState("status");
  const [listMonthFilter, setListMonthFilter] = useState("");
  const defaultedListMonth = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vehicles");
      setVehicles(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Compute the most-recent list_month present in the vehicle data.
  // Used as the default filter + Profit panel default.
  const latestListMonth = (() => {
    const months = vehicles
      .map((v) => normalizeListMonth(v))
      .filter(Boolean)
      .sort();
    return months[months.length - 1] || "";
  })();

  // On first load, pre-filter the table to the latest list_month present
  // in the data. Users can still clear the filter to see all vehicles.
  useEffect(() => {
    if (defaultedListMonth.current) return;
    if (!latestListMonth) return;
    setListMonthFilter(latestListMonth);
    defaultedListMonth.current = true;
  }, [latestListMonth]);

  const updateStatus = async (vehicleId, newStatus) => {    // Optimistic update
    setVehicles((prev) =>
      prev.map((v) => (v.id === vehicleId ? { ...v, status: newStatus } : v)),
    );
    try {
      await api.patch(`/vehicles/${vehicleId}`, { status: newStatus });
      toast.success(
        `Status updated to ${(statusMeta[newStatus] || {}).label || newStatus}`,
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load(); // revert on failure
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        mileage: Number(form.mileage || 0),
        purchase_price: Number(form.purchase_price || 0),
        sale_price: form.sale_price === "" ? null : Number(form.sale_price),
        sold_date: form.sold_date || null,
        list_month: form.list_month || null,
        title: form.title || "",
      };
      await api.post("/vehicles", payload);
      toast.success("Vehicle added");
      setOpen(false);
      setForm(emptyVehicle);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const filteredVehicles = listMonthFilter
    ? vehicles.filter((v) => normalizeListMonth(v) === listMonthFilter)
    : vehicles;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <PageHeader
        eyebrow="Fleet"
        title="Vehicles"
        description="Every vehicle you have, expenses, and running profit — in one view."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-vehicle-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                <Plus className="w-4 h-4 mr-2" /> Add Vehicle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] flex flex-col p-0">
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-200 flex-shrink-0">
                <DialogTitle className="font-heading text-2xl tracking-tight">New vehicle</DialogTitle>
              </DialogHeader>
              <form onSubmit={onCreate} className="flex flex-col flex-1 min-h-0" data-testid="vehicle-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-4 overflow-y-auto flex-1">
                <div className="col-span-2">
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">VIN</Label>
                  <Input required value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} data-testid="vehicle-vin-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">
                    Display Title <span className="text-zinc-400 normal-case tracking-normal font-normal">(optional — overrides &quot;Year Make Model&quot;)</span>
                  </Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder={`e.g. "Project Turbo" or "Andre's build"`}
                    data-testid="vehicle-title-input"
                    className="rounded-sm border-zinc-300"
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Make</Label>
                  <Input required value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} data-testid="vehicle-make-input" className="rounded-sm border-zinc-300" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Model</Label>
                  <Input required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="vehicle-model-input" className="rounded-sm border-zinc-300" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Year</Label>
                  <Input required type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Mileage</Label>
                  <Input type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Color</Label>
                  <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="rounded-sm border-zinc-300" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="vehicle-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">List Month</Label>
                  <Input
                    type="month"
                    value={form.list_month}
                    onChange={(e) => setForm({ ...form, list_month: e.target.value })}
                    data-testid="vehicle-list-month-input"
                    className="rounded-sm border-zinc-300 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Purchase Price</Label>
                  <Input required type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="vehicle-purchase-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Sale Price (if sold)</Label>
                  <Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="vehicle-sale-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Sold Date</Label>
                  <Input
                    type="date"
                    value={form.sold_date}
                    onChange={(e) => setForm({ ...form, sold_date: e.target.value })}
                    data-testid="vehicle-sold-date-input"
                    className="rounded-sm border-zinc-300 font-mono"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="vehicle-notes-input" className="rounded-sm border-zinc-300" rows={3} />
                </div>
                </div>
                <DialogFooter className="px-6 py-4 border-t border-zinc-200 bg-white flex-shrink-0">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="vehicle-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                    {saving ? "Adding..." : "Add vehicle"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs text-zinc-500 font-mono">
            {filteredVehicles.length}
            {listMonthFilter ? ` of ${vehicles.length}` : ""}{" "}
            {filteredVehicles.length === 1 ? "vehicle" : "vehicles"}
          </div>
          {listMonthFilter && (
            <button
              type="button"
              onClick={() => setListMonthFilter("")}
              data-testid="clear-list-month-filter"
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.15em] uppercase font-bold bg-[#002FA7] text-white rounded-sm hover:bg-[#002075]"
              title="Clear filter"
            >
              List month: {formatListMonth(listMonthFilter)}
              <span aria-hidden="true" className="ml-1 leading-none">×</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Sort</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-sm border-zinc-300 w-[220px]" data-testid="vehicle-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isAdmin && (
        <ProfitByListMonth
          vehicles={vehicles}
          selectedMonth={listMonthFilter}
          setSelectedMonth={setListMonthFilter}
          latestMonth={latestListMonth}
        />
      )}

      <div className="mt-4 bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {["Status", "List Month", "Vehicle", "Purchase", "Expenses", "Sale", "Profit", "Notes", "Sold Date"].map((h) => {
                if (h === "List Month") {
                  const active = sortBy === "list_month_asc" || sortBy === "list_month_desc";
                  const arrow = sortBy === "list_month_desc" ? "↓" : sortBy === "list_month_asc" ? "↑" : "";
                  return (
                    <th key={h} className="text-left px-4 py-3 align-top">
                      <button
                        type="button"
                        onClick={() =>
                          setSortBy(sortBy === "list_month_asc" ? "list_month_desc" : "list_month_asc")
                        }
                        data-testid="sort-by-list-month"
                        title="Click to sort by list month"
                        className={`text-[10px] tracking-[0.2em] uppercase font-bold inline-flex items-center gap-1 hover:text-[#002FA7] ${
                          active ? "text-[#002FA7]" : "text-zinc-500"
                        }`}
                      >
                        {h}
                        {arrow && <span className="font-mono">{arrow}</span>}
                      </button>
                    </th>
                  );
                }
                return (
                  <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 align-top">{h}</th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {loading && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-zinc-500">Loading...</td></tr>
            )}
            {!loading && filteredVehicles.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-zinc-500">
                {listMonthFilter
                  ? `No vehicles match list month ${formatListMonth(listMonthFilter)}.`
                  : "No vehicles yet. Add one to get started."}
              </td></tr>
            )}
            {filteredVehicles
              .slice()
              .sort((a, b) => compareVehicles(a, b, sortBy))
              .map((v) => (
              <tr key={v.id} className="hover:bg-zinc-50 align-top" data-testid={`vehicle-row-${v.vin}`}>
                <td className="px-4 py-3">
                  <div className="relative inline-block">
                    <select
                      value={v.status}
                      onChange={(e) => updateStatus(v.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`vehicle-status-cell-${v.vin}`}
                      title="Click to change status"
                      className={`appearance-none cursor-pointer pr-6 pl-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded-sm border focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${(statusMeta[v.status] || statusMeta.in_inventory).className}`}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {(statusMeta[o.value] || {}).label || o.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="w-3 h-3 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-mono text-zinc-700" data-testid={`vehicle-list-month-${v.vin}`}>
                  {(() => {
                    const norm = normalizeListMonth(v);
                    if (!norm) return "—";
                    return (
                      <button
                        type="button"
                        onClick={() => setListMonthFilter(norm === listMonthFilter ? "" : norm)}
                        title={norm === listMonthFilter ? "Clear filter" : `Filter to ${formatListMonth(norm)}`}
                        data-testid={`filter-list-month-${v.vin}`}
                        className={`underline-offset-2 hover:underline ${
                          norm === listMonthFilter
                            ? "font-bold text-[#002FA7] underline"
                            : "text-zinc-700 hover:text-[#002FA7]"
                        }`}
                      >
                        {formatListMonth(norm)}
                      </button>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 text-sm">
                  <Link
                    to={`/vehicles/${v.id}`}
                    data-testid={`open-vehicle-${v.vin}`}
                    className="font-semibold text-zinc-950 hover:text-[#002FA7] hover:underline"
                  >
                    {vehicleTitle(v)}
                  </Link>
                  {v.title && v.title.trim() && (
                    <div className="text-xs text-zinc-500 font-mono">{v.year} {v.make} {v.model}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-zinc-900">{currency(v.purchase_price)}</td>
                <td className="px-4 py-3 text-sm font-mono text-zinc-700">{currency(v.total_expenses)}</td>
                <td className="px-4 py-3 text-sm font-mono text-zinc-900">{v.sale_price != null ? currency(v.sale_price) : "—"}</td>
                <td className={`px-4 py-3 text-sm font-mono font-bold ${v.profit >= 0 ? "text-[#10B981]" : "text-[#FF2400]"}`}>
                  {currency(v.profit)}
                </td>
                <td className="px-4 py-3 text-sm text-zinc-700 max-w-xs">
                  {v.notes ? (
                    <span className="line-clamp-2 whitespace-pre-wrap" title={v.notes}>{v.notes}</span>
                  ) : (
                    <span className="text-zinc-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-zinc-700">{v.sold_date || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProfitByListMonth({ vehicles, selectedMonth, setSelectedMonth, latestMonth }) {
  // Current calendar month "YYYY-MM"
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const defaultMonth = latestMonth || currentMonthKey;

  // Shift the selected month by N months (negative = past, positive = future)
  const shiftMonth = (delta) => {
    const [y, m] = (selectedMonth || defaultMonth).split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    setSelectedMonth(key);
  };

  const activeMonth = selectedMonth || defaultMonth;

  const monthLabel = (() => {
    const [y, m] = activeMonth.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  })();

  const isDefaultMonth = activeMonth === defaultMonth;
  const defaultLabel = (() => {
    const [y, m] = defaultMonth.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  })();

  // Group vehicles with a list_month matching activeMonth
  const groups = (() => {
    const map = new Map();
    for (const v of vehicles) {
      const norm = normalizeListMonth(v);
      if (!norm) continue;
      if (norm !== activeMonth) continue;
      if (!map.has(norm)) {
        map.set(norm, {
          month: norm,
          count: 0,
          sold: 0,
          realized: 0,
          expected: 0,
          revenueSold: 0,      // total sale_price for sold vehicles this month
          revenueExpected: 0,  // total sale_price for pending vehicles that have a sale target
        });
      }
      const g = map.get(norm);
      g.count += 1;
      if (v.status === "sold") {
        g.sold += 1;
        g.realized += Number(v.profit || 0);
        g.revenueSold += Number(v.sale_price || 0);
      } else if (v.sale_price != null) {
        // Projected profit for vehicles with a sale target but not yet sold
        const projected =
          Number(v.sale_price || 0) -
          Number(v.purchase_price || 0) -
          Number(v.total_expenses || 0);
        g.expected += projected;
        g.revenueExpected += Number(v.sale_price || 0);
      }
    }
    return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
  })();

  const totals = groups.reduce(
    (acc, g) => {
      acc.count += g.count;
      acc.sold += g.sold;
      acc.realized += g.realized;
      acc.expected += g.expected;
      acc.revenueSold += g.revenueSold;
      acc.revenueExpected += g.revenueExpected;
      return acc;
    },
    { count: 0, sold: 0, realized: 0, expected: 0, revenueSold: 0, revenueExpected: 0 },
  );

  return (
    <div className="mt-6" data-testid="profit-by-list-month">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-3">
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-zinc-950">
            Profit by List Month
          </h2>
          <div className="text-sm text-zinc-500 mt-1">
            {monthLabel}
            {!isDefaultMonth && (
              <button
                onClick={() => setSelectedMonth(defaultMonth)}
                data-testid="profit-month-today"
                className="ml-3 text-[10px] tracking-[0.2em] uppercase font-bold text-[#002FA7] hover:underline"
              >
                Back to {defaultLabel}
              </button>
            )}
            <span className="block text-xs text-zinc-500 mt-1">
              Realized = sold profit; Expected = projected from sale price minus purchase + expenses.
            </span>
          </div>
          <div className="mt-3 flex items-center gap-1" data-testid="profit-month-picker">
            <button
              onClick={() => shiftMonth(-1)}
              data-testid="profit-month-prev"
              className="h-8 w-8 flex items-center justify-center border border-zinc-300 bg-white hover:bg-zinc-50 rounded-sm"
              title="Previous month"
            >
              <ChevronLeft className="w-4 h-4 text-zinc-700" />
            </button>
            <div
              className="h-8 min-w-[180px] px-3 flex items-center justify-center border border-zinc-300 bg-white font-mono font-semibold text-sm text-zinc-950"
              data-testid="profit-month-label"
            >
              {monthLabel}
            </div>
            <button
              onClick={() => shiftMonth(1)}
              data-testid="profit-month-next"
              disabled={activeMonth >= currentMonthKey}
              className="h-8 w-8 flex items-center justify-center border border-zinc-300 bg-white hover:bg-zinc-50 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
              title="Next month"
            >
              <ChevronRight className="w-4 h-4 text-zinc-700" />
            </button>
          </div>
        </div>
        {groups.length > 0 && (
          <div className="text-right">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
              Total ({totals.count} {totals.count === 1 ? "vehicle" : "vehicles"})
            </div>
            <div
              className={`font-mono font-black text-lg ${
                totals.realized + totals.expected >= 0 ? "text-[#10B981]" : "text-[#FF2400]"
              }`}
              data-testid="profit-by-list-month-total"
            >
              {currency(totals.realized + totals.expected)}
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mt-1">
              Revenue
            </div>
            <div
              className="font-mono font-bold text-sm text-zinc-900"
              data-testid="revenue-by-list-month-total"
            >
              {currency(totals.revenueSold + totals.revenueExpected)}
            </div>
          </div>
        )}
      </div>

      {groups.length === 0 ? (
        <div className="mt-4 bg-white border border-zinc-200 rounded-sm px-4 py-8 text-center text-sm text-zinc-500">
          No vehicles listed for {monthLabel} yet. Set a vehicle&apos;s List Month to this month to see it here.
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => {
            const net = g.realized + g.expected;
            return (
              <div
                key={g.month}
                className="bg-white border border-zinc-200 rounded-sm overflow-hidden"
                data-testid={`profit-month-${g.month}`}
              >
                <div className="flex items-center justify-between px-4 py-3 bg-zinc-950 text-white">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">
                      List Month
                    </div>
                    <div className="font-mono font-bold text-white text-base mt-0.5">
                      {formatListMonth(g.month)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400">
                      Net
                    </div>
                    <div
                      className={`font-mono font-black text-lg ${
                        net >= 0 ? "text-emerald-400" : "text-red-400"
                      }`}
                      data-testid={`profit-month-net-${g.month}`}
                    >
                      {currency(net)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 divide-x divide-zinc-200">
                  <div className="px-4 py-3">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                      Realized
                    </div>
                    <div
                      className={`font-mono font-bold text-sm mt-1 ${
                        g.realized >= 0 ? "text-[#10B981]" : "text-[#FF2400]"
                      }`}
                    >
                      {currency(g.realized)}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {g.sold} sold
                    </div>
                  </div>
                  <div className="px-4 py-3">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                      Expected
                    </div>
                    <div className="font-mono font-bold text-sm text-zinc-700 mt-1">
                      {currency(g.expected)}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {g.count - g.sold} pending
                    </div>
                  </div>
                </div>
                <div className="border-t border-zinc-200 px-4 py-3 bg-zinc-50">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
                      Revenue
                    </div>
                    <div
                      className="font-mono font-bold text-sm text-zinc-900"
                      data-testid={`revenue-month-${g.month}`}
                    >
                      {currency(g.revenueSold + g.revenueExpected)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-500">
                    <span>Sold: {currency(g.revenueSold)}</span>
                    <span>Expected: {currency(g.revenueExpected)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
